import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { ModalPortal } from '@/components/ModalPortal';
import { Sidebar, type SidebarItem } from '@/components/Sidebar/Sidebar';
import { useT } from '@/core/providers/I18nProvider';
import { useFocusTrap } from '@/hooks/useFocusTrap';

import { BrandMark } from './BrandMark';

import type { ReactElement, RefObject } from 'react';

export interface MobileNavDrawerProps {
  /** Whether the drawer is open. Closed renders nothing. */
  open: boolean;
  /** Nav items, same shape the desktop Sidebar consumes. */
  items: readonly SidebarItem[];
  /** Close request — scrim click, close button, or Escape. */
  onClose: () => void;
  /**
   * The topbar hamburger that opened the drawer. Focus returns to it when the
   * drawer closes (ARIA APG dialog pattern) — it sits UNDER the overlay while
   * the drawer is open, so it cannot keep focus itself.
   */
  returnFocusRef: RefObject<HTMLButtonElement>;
}

/**
 * Full-screen mobile navigation drawer (the sidebar's <768px form, toggled
 * from the TopBar hamburger). A modal surface: scrim + `role="dialog"` panel
 * with a header (brand + explicit close button) above the scrolling nav.
 *
 * Owns the full modal contract for its markup: focus containment (WCAG
 * 2.4.3), initial focus on the close button, focus restoration to the
 * trigger, and Escape-to-close (A17 live-pass finding — the scrim is the only
 * pointer escape hatch outside the header's close button, and the topbar sits
 * under the overlay, so keyboard users had no way out at all).
 *
 * Portaled to document.body (ui-design.md §11): a `fixed inset-0` overlay must
 * escape the page layout tree so no ancestor margin can offset the scrim.
 * React context (theme, i18n) still flows through the portal.
 */
export function MobileNavDrawer({
  open,
  items,
  onClose,
  returnFocusRef,
}: MobileNavDrawerProps): ReactElement | null {
  const { t } = useT();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Modal focus containment: the drawer is `aria-modal`, so Tab must not
  // escape to the chrome behind the scrim.
  useFocusTrap(drawerRef, open);

  // Initial focus + restoration. Guarded by `wasOpen` so mount never steals
  // focus (a known regression class — a bare else branch runs on mount).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
    } else if (wasOpen.current) {
      returnFocusRef.current?.focus({ preventScroll: true });
    }
    wasOpen.current = open;
  }, [open, returnFocusRef]);

  // Modal keyboard contract: Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <ModalPortal>
      <div id="app-shell-drawer" className="fixed inset-0 z-40 flex">
        <button
          type="button"
          aria-label={t('appshell.closeMenu')}
          tabIndex={-1}
          onClick={onClose}
          className="absolute inset-0 cursor-default bg-overlay motion-safe:animate-fade-in"
        />
        <div
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('appshell.navigation')}
          className="relative flex h-full w-sidebar max-w-[85vw] flex-col bg-surface shadow-elevation-3 motion-safe:animate-fade-in"
        >
          {/* Drawer header: brand presence + an explicit close affordance.
              Without it the drawer opened straight into nav rows and the only
              pointer escape hatch was the scrim (the topbar's toggle sits UNDER
              the overlay). */}
          <div className="flex h-topbar shrink-0 items-center justify-between border-b border-hairline px-3">
            <BrandMark wordmark="always" />
            <button
              ref={closeRef}
              type="button"
              aria-label={t('appshell.closeMenu')}
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
          <Sidebar
            items={items}
            collapsed={false}
            ariaLabel={t('appshell.mobileSidebar')}
            className="h-auto min-h-0 w-full flex-1 border-e-0"
          />
        </div>
      </div>
    </ModalPortal>
  );
}
